/**
 * Page de gestion des consignes/vidanges
 * Suivi des emballages (caisses EPS, palettes), soldes clients, mouvements
 */

import { useState, useEffect } from 'react';
import { formatPrice } from '../utils/pos.utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Package,
  Plus,
  Search,
  TrendingUp,
  TrendingDown,
  Users,
  Archive,
  AlertCircle,
  CheckCircle,
  ArrowUpDown,
  DollarSign,
  Edit,
  BarChart3,
} from 'lucide-react';

// Tenant ID temporaire - à remplacer par le contexte utilisateur
const TEMP_TENANT_ID = '260167c8-c194-4491-8853-fc07a193b4b6';

interface PackagingType {
  id: string;
  tenant_id: string;
  code: string;
  name: string;
  description?: string;
  deposit_cents: number;
  is_returnable: boolean;
  is_reusable: boolean;
  max_uses?: number;
  is_active: boolean;
  created_at: string;
}

interface CustomerBalance {
  id: string;
  tenant_id: string;
  customer_id: string;
  packaging_type_id: string;
  total_delivered: number;
  total_returned: number;
  current_balance: number;
  deposit_cents: number;
  last_movement_date: string;
  created_at: string;
  updated_at: string;
  customer?: {
    id: string;
    name: string;
    email?: string;
  };
  packaging_type?: PackagingType;
}

interface PackagingMovement {
  id: string;
  tenant_id: string;
  customer_id: string;
  packaging_type_id: string;
  movement_type: 'DELIVERY' | 'RETURN' | 'ADJUSTMENT' | 'LOSS' | 'EXCHANGE';
  quantity: number;
  deposit_cents_per_unit: number;
  total_deposit_cents: number;
  delivery_note_number?: string;
  sale_id?: string;
  order_id?: string;
  notes?: string;
  created_by: string;
  created_at: string;
  customer?: {
    name: string;
  };
  packaging_type?: {
    code: string;
    name: string;
  };
}

interface PackagingStats {
  total_types: number;
  active_types: number;
  total_customers_with_balance: number;
  total_packaging_in_circulation: number;
  total_deposit_value_cents: number;
  by_type: Array<{
    packaging_type_id: string;
    packaging_type_code: string;
    packaging_type_name: string;
    total_in_circulation: number;
    deposit_value_cents: number;
  }>;
}

export default function PackagingManagement() {
  const [packagingTypes, setPackagingTypes] = useState<PackagingType[]>([]);
  const [balances, setBalances] = useState<CustomerBalance[]>([]);
  const [movements, setMovements] = useState<PackagingMovement[]>([]);
  const [stats, setStats] = useState<PackagingStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'balances' | 'movements' | 'types' | 'stats'>('balances');

  // Modals
  const [showCreateTypeModal, setShowCreateTypeModal] = useState(false);
  const [showRecordMovementModal, setShowRecordMovementModal] = useState(false);

  useEffect(() => {
    loadPackagingTypes();
    loadBalances();
    loadStats();
    if (activeTab === 'movements') {
      loadMovements();
    }
  }, [activeTab]);

  const loadPackagingTypes = async () => {
    try {
      const response = await fetch(`http://localhost:3000/api/packaging/types?tenantId=${TEMP_TENANT_ID}`);
      if (!response.ok) throw new Error('Erreur lors du chargement des types');
      const data = await response.json();
      setPackagingTypes(data || []);
    } catch (error) {
      console.error('Erreur chargement types:', error);
      alert('Erreur lors du chargement des types d\'emballages');
    }
  };

  const loadBalances = async () => {
    try {
      setLoading(true);
      const response = await fetch(`http://localhost:3000/api/packaging/balances?tenantId=${TEMP_TENANT_ID}`);
      if (!response.ok) throw new Error('Erreur lors du chargement des soldes');
      const data = await response.json();
      setBalances(data || []);
    } catch (error) {
      console.error('Erreur chargement soldes:', error);
      alert('Erreur lors du chargement des soldes');
    } finally {
      setLoading(false);
    }
  };

  const loadMovements = async () => {
    try {
      setLoading(true);
      // Note: Pour l'instant on charge tous les mouvements récents
      // Il faudra améliorer avec pagination
      const response = await fetch(`http://localhost:3000/api/packaging/movements?tenantId=${TEMP_TENANT_ID}&limit=100`);
      if (!response.ok) throw new Error('Erreur lors du chargement des mouvements');
      const data = await response.json();
      setMovements(data || []);
    } catch (error) {
      console.error('Erreur chargement mouvements:', error);
      alert('Erreur lors du chargement des mouvements');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`http://localhost:3000/api/packaging/stats?tenantId=${TEMP_TENANT_ID}`);
      if (!response.ok) throw new Error('Erreur lors du chargement des stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const getMovementTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      DELIVERY: 'Livraison',
      RETURN: 'Retour',
      ADJUSTMENT: 'Ajustement',
      LOSS: 'Perte',
      EXCHANGE: 'Échange',
    };
    return labels[type] || type;
  };

  const getMovementTypeIcon = (type: string) => {
    switch (type) {
      case 'DELIVERY':
        return <TrendingUp className="w-4 h-4 text-red-600" />;
      case 'RETURN':
        return <TrendingDown className="w-4 h-4 text-green-600" />;
      case 'ADJUSTMENT':
        return <Edit className="w-4 h-4 text-blue-600" />;
      case 'LOSS':
        return <AlertCircle className="w-4 h-4 text-orange-600" />;
      case 'EXCHANGE':
        return <ArrowUpDown className="w-4 h-4 text-purple-600" />;
      default:
        return <Package className="w-4 h-4 text-gray-600" />;
    }
  };

  // Filtrer les soldes par recherche
  const filteredBalances = balances.filter((balance) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      balance.customer?.name.toLowerCase().includes(searchLower) ||
      balance.packaging_type?.name.toLowerCase().includes(searchLower) ||
      balance.packaging_type?.code.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion des Consignes</h1>
          <p className="mt-1 text-sm text-gray-500">
            Suivi des emballages consignés (caisses EPS, palettes, etc.)
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowRecordMovementModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <ArrowUpDown className="w-5 h-5" />
            Enregistrer mouvement
          </button>
          <button
            onClick={() => setShowCreateTypeModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Nouveau type
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Types actifs</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active_types}</p>
              </div>
              <Package className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Clients avec solde</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_customers_with_balance}</p>
              </div>
              <Users className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">En circulation</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_packaging_in_circulation}</p>
              </div>
              <Archive className="w-8 h-8 text-orange-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Valeur totale</p>
                <p className="text-2xl font-bold text-gray-900">{formatPrice(stats.total_deposit_value_cents)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        {/* Onglets : ils défilent de côté sur téléphone plutôt que de pousser la page. */}
        <nav className="-mb-px flex gap-6 sm:gap-8 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button
            onClick={() => setActiveTab('balances')}
            className={`shrink-0 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'balances'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Soldes Clients
            </div>
          </button>
          <button
            onClick={() => setActiveTab('movements')}
            className={`shrink-0 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'movements'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-5 h-5" />
              Mouvements
            </div>
          </button>
          <button
            onClick={() => setActiveTab('types')}
            className={`shrink-0 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'types'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Types d'emballages
            </div>
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`shrink-0 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'stats'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Statistiques
            </div>
          </button>
        </nav>
      </div>

      {/* Balances Tab */}
      {activeTab === 'balances' && (
        <>
          {/* Search */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un client, type d'emballage..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Balances List */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Chargement...</div>
            ) : filteredBalances.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>Aucun solde trouvé</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type d'emballage
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Livrés
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Retournés
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Solde actuel
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Valeur consigne
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dernier mvt
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredBalances.map((balance) => (
                      <tr key={balance.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {balance.customer?.name || 'Client inconnu'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {balance.packaging_type?.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {balance.packaging_type?.code}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center gap-1">
                            <TrendingUp className="w-4 h-4 text-red-600" />
                            {balance.total_delivered}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center gap-1">
                            <TrendingDown className="w-4 h-4 text-green-600" />
                            {balance.total_returned}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-sm font-medium rounded-full ${
                              balance.current_balance > 0
                                ? 'bg-orange-100 text-orange-800'
                                : balance.current_balance < 0
                                ? 'bg-red-100 text-red-800'
                                : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {balance.current_balance}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatPrice(balance.deposit_cents)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {format(new Date(balance.last_movement_date), 'dd/MM/yyyy', { locale: fr })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Movements Tab */}
      {activeTab === 'movements' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Chargement...</div>
          ) : movements.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <ArrowUpDown className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>Aucun mouvement trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Emballage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantité
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valeur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      BL/Référence
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {movements.map((movement) => (
                    <tr key={movement.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(movement.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getMovementTypeIcon(movement.movement_type)}
                          <span className="text-sm">{getMovementTypeLabel(movement.movement_type)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {movement.customer?.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {movement.packaging_type?.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {movement.packaging_type?.code}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {movement.quantity > 0 ? `+${movement.quantity}` : movement.quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatPrice(movement.total_deposit_cents)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {movement.delivery_note_number || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Types Tab */}
      {activeTab === 'types' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {packagingTypes.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>Aucun type d'emballage configuré</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nom
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valeur consigne
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Retournable
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {packagingTypes.map((type) => (
                    <tr key={type.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {type.code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {type.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {type.description || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatPrice(type.deposit_cents)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {type.is_returnable ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-gray-400" />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            type.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {type.is_active ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && stats && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par type d'emballage</h3>
            <div className="space-y-4">
              {stats.by_type.map((item) => (
                <div key={item.packaging_type_id} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Package className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{item.packaging_type_name}</span>
                      <span className="text-xs text-gray-500">({item.packaging_type_code})</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${(item.total_in_circulation / stats.total_packaging_in_circulation) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="ml-4 text-right">
                    <div className="text-sm font-medium text-gray-900">{item.total_in_circulation}</div>
                    <div className="text-xs text-gray-500">{formatPrice(item.deposit_value_cents)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TODO: Modals pour Create Type / Record Movement */}
      {showCreateTypeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h2 className="text-xl font-bold mb-4">Nouveau type d'emballage</h2>
            <p className="text-gray-600">Formulaire à implémenter...</p>
            <button
              onClick={() => setShowCreateTypeModal(false)}
              className="mt-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {showRecordMovementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full">
            <h2 className="text-xl font-bold mb-4">Enregistrer un mouvement</h2>
            <p className="text-gray-600">Formulaire à implémenter...</p>
            <button
              onClick={() => setShowRecordMovementModal(false)}
              className="mt-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
